import React from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar } from 'recharts';
import type { DiretoriaIndicator } from '@/data/diretoriaTypes';
import { diretoriaSparklineStroke } from '@/lib/diretoriaKpiTone';

/** Prévia compacta no cartão alinhada ao tipo de gráfico do modal. */
export const DiretoriaSparkline: React.FC<{ indicator: DiretoriaIndicator }> = ({ indicator }) => {
  const pk = indicator.primaryKpiIndex ?? 0;
  const stroke = diretoriaSparklineStroke(indicator.kpis?.[pk]?.tone);
  const h = 40;

  if (indicator.chartKind === 'none') {
    return <div className="h-10 w-full rounded-md bg-muted/40" aria-hidden />;
  }

  // Composição (donut): mini barras da distribuição — pizza minúscula é ilegível.
  if (indicator.chartKind === 'pie') {
    const pie = indicator.pieData?.length
      ? indicator.pieData
      : (indicator.series ?? []).map((s) => ({ name: s.name, value: s.valor }));
    if (!pie.length) {
      return <div className="h-10 w-full rounded-md bg-muted/40" aria-hidden />;
    }
    const data = pie.slice(0, 10).map((p) => ({ name: p.name, valor: p.value }));
    return (
      <div className="w-full" style={{ height: h }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
            <Bar dataKey="valor" fill={stroke} radius={[2, 2, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (!indicator.series?.length) {
    return <div className="h-10 w-full rounded-md bg-muted/40" aria-hidden />;
  }

  const data = indicator.series.slice(-10);
  const useLine =
    indicator.chartKind === 'line' ||
    (indicator.chartKind === 'barGrouped' && data.length >= 4);

  if (useLine) {
    return (
      <div className="w-full" style={{ height: h }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 2, left: 0, bottom: 0 }}>
            <Line
              type="monotone"
              dataKey="valor"
              stroke={stroke}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
            {data.some((d) => d.valor2 != null) ? (
              <Line
                type="monotone"
                dataKey="valor2"
                stroke={stroke}
                strokeWidth={1}
                strokeOpacity={0.45}
                dot={false}
                isAnimationActive={false}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // bar / barHorizontal / barGrouped curto: mini barras
  const key = indicator.barSeriesKey ?? 'valor';
  return (
    <div className="w-full" style={{ height: h }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
          <Bar dataKey={key} fill={stroke} radius={[2, 2, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
